import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { FaExternalLinkAlt } from "react-icons/fa";

interface TokensPrice {
  nbmPrice: string;
  assetPriceBSC: string;
  assetPriceHedera: string;
}

interface BurnData {
  nbmBurnedPercentage: string;
  assetBurnedPercentage: string;
}

const TokensInfo = () => {
  const [burnData, setBurnData] = useState<BurnData | null>(null);
  const [tokensPrice, setTokensPrice] = useState<TokensPrice | null>(null);

  // Function to format number with commas like the price format
  const formatPriceWithCommas = (value: number): string => {
    const formatted = value.toFixed(10);
    const [whole, decimal] = formatted.split(".");

    if (!decimal) return formatted;

    // Add commas every 3 digits in the decimal part
    let result = "";
    for (let i = 0; i < decimal.length; i += 3) {
      if (i > 0) result += ",";
      result += decimal.substr(i, 3);
    }

    // Remove trailing zeros and commas
    const cleanDecimal = result.replace(/0+$/, "").replace(/,$/, "");

    return `${whole}.${cleanDecimal}`;
  };

  const ASSET_BURN_PROOF =
    "https://bscscan.com/token/0x6b471d5Ab9f3d92A600e7d49A0b135BF6D4c6A5b?a=0x000000000000000000000000000000000000dead";
  const NBM_BURN_PROOF =
    "https://bscscan.com/token/0x12da2f2761038486271c99da7e0fb4413e2b5e38?a=0x000000000000000000000000000000000000dead";

  useEffect(() => {
    const fetchBurnData = async () => {
      try {
        const response = await axios.get("/burnData.json");
        setBurnData(response.data);
      } catch (error) {
        console.error("Error fetching burn data:", error);
      }
    };

    const fetchTokensPrice = async () => {
      try {
        const response = await axios.get("/tokensPrice.json");
        // Map the old API response format to new variable names
        const data = response.data;
        setTokensPrice({
          nbmPrice: data.nbmPricePancake,
          assetPriceBSC: data.assetPriceCMC,
          assetPriceHedera: data.assetPriceSaucer,
        });
      } catch (error) {
        console.error("Error fetching tokens price:", error);
      }
    };

    fetchBurnData();
    fetchTokensPrice();

    // Optional: Refresh data every 5 minutes
    const interval = setInterval(() => {
      fetchBurnData();
      fetchTokensPrice();
    }, 5 * 60 * 1000); // 5 minutes in milliseconds

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-10 max-w-4xl mx-auto">
      {/* Tokens Price Section */}
      <section>
        <h3 className="text-2xl font-bold mb-5 text-gray-900 text-center">
          💰 Token Market Prices
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="p-3 bg-white/80 rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
          >
            <h4 className="text-base font-semibold text-gray-800 mb-2">
              NBM (BSC)
            </h4>
            <p className="text-2xl font-extrabold text-purple-600 mb-2">
              ${tokensPrice?.nbmPrice ?? "--"}
            </p>
            <div className="text-xs text-gray-700 space-y-1">
              <p>Exchange Rate:</p>
              <p className="font-medium text-purple-700">
                1000 NBM = 1 ASSET(HTS)
              </p>
              <p className="font-medium text-purple-700">
                1000 NBM ≈ $
                {tokensPrice?.nbmPrice
                  ? formatPriceWithCommas(
                      parseFloat(tokensPrice.nbmPrice.replace(/,/g, "")) * 1000
                    )
                  : "--"}
              </p>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="p-3 bg-white/80 rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
          >
            <h4 className="text-base font-semibold text-gray-800 mb-2">
              ASSET (BSC)
            </h4>
            <p className="text-2xl font-extrabold text-orange-500 mb-2">
              ${tokensPrice?.assetPriceBSC ?? "--"}
            </p>
            <p className="text-xs text-gray-600">
              Parity: 1 ASSET(BSC) = 1 ASSET(HTS)
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="p-3 bg-white/80 rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
          >
            <h4 className="text-base font-semibold text-gray-800 mb-2">
              ASSET (HTS)
            </h4>
            <p className="text-2xl font-extrabold text-teal-600 mb-1">
              ${tokensPrice?.assetPriceHedera ?? "--"}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Burn Progress Section */}
      <section>
        <h3 className="text-2xl font-bold mb-5 text-gray-900 text-center">
          🔥 Burn Progress Tracker
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="relative overflow-hidden p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <h4 className="text-base font-semibold text-gray-800 mb-2">
              NBM (BSC)
            </h4>
            <div className="flex items-center justify-between">
              <p className="text-2xl font-extrabold text-purple-700">
                {burnData?.nbmBurnedPercentage ?? "--"}%
              </p>
              <motion.a
                href={NBM_BURN_PROOF}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700"
                whileHover={{ x: 3 }}
              >
                Proof <FaExternalLinkAlt size={12} />
              </motion.a>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="relative overflow-hidden p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <h4 className="text-base font-semibold text-gray-800 mb-2">
              ASSET (BSC)
            </h4>
            <div className="flex items-center justify-between">
              <p className="text-2xl font-extrabold text-orange-600">
                {burnData?.assetBurnedPercentage ?? "--"}%
              </p>
              <motion.a
                href={ASSET_BURN_PROOF}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600"
                whileHover={{ x: 3 }}
              >
                Proof <FaExternalLinkAlt size={12} />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TokensInfo;
